import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { useOnboarding, useAcceptAgreement, useOnboardingCheckout, useSaveOnboarding } from '../../hooks/useOnboarding';
import { FileText, CheckCircle2, Loader2, ShieldCheck, ArrowRight, CreditCard, Minus, Plus } from 'lucide-react';
import { track, trackConversion } from '../../lib/analytics';

const BASE_PRICE = 49;
const EXTRA_PRICE = 15;
const YEARLY_MONTHS = 11;
const MAX_UNITS = 50;
const DESIGN_FEE = 189;        // one-time, per additional property (first site included)
const monthlyFor = (units: number) => BASE_PRICE + EXTRA_PRICE * (Math.max(1, units) - 1);
// One-time design fee for a separate website per property: €189 × (units − 1).
const designFeeFor = (units: number) => DESIGN_FEE * Math.max(0, units - 1);

type SiteModel = 'combined' | 'per_property';

function storedPlan(): { units?: number; billing?: 'monthly' | 'yearly'; siteModel?: SiteModel } {
  try { return JSON.parse(localStorage.getItem('kx_plan') || '{}'); } catch { return {}; }
}

// Combined "Acuerdo y pago" step: sign the agreement, then the payment unlocks inline
// on the same page — fewer clicks, one continuous flow. Default 1 property (€49).
export default function AgreementPage() {
  const { t } = useTranslation();
  const { user } = useClientAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useOnboarding();
  const accept = useAcceptAgreement();
  const checkout = useOnboardingCheckout();
  const save = useSaveOnboarding();

  const [typedName, setTypedName] = useState('');
  const [checked, setChecked] = useState(false);
  const [units, setUnits] = useState(1);
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const [siteModel, setSiteModel] = useState<SiteModel>('combined');
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (hydrated || !data) return;
    const plan = storedPlan();
    setUnits(data.payment.units || plan.units || 1); // default 1 property
    setBilling(data.payment.billing || plan.billing || 'monthly');
    // Prefer the stored profile value; fall back to the landing-page choice; default combined.
    setSiteModel(data.payment.siteModel || plan.siteModel || 'combined');
    setHydrated(true);
  }, [data, hydrated]);

  const signed = data?.agreement.signed ?? false;
  const paid = data?.payment.status === 'paid';
  const monthly = monthlyFor(units);
  const total = billing === 'yearly' ? monthly * YEARLY_MONTHS : monthly;
  const designFee = siteModel === 'per_property' ? designFeeFor(units) : 0;

  // Meta AddPaymentInfo — fires once when the payment step unlocks (signed, not
  // yet paid). Placed BEFORE the loading early-return: a hook after a
  // conditional return changes the hook order between renders (rules-of-hooks).
  useEffect(() => {
    if (!data || !signed || paid) return;
    try {
      if (sessionStorage.getItem('kx_evt_addpaymentinfo')) return;
      sessionStorage.setItem('kx_evt_addpaymentinfo', '1');
    } catch { /* ignore */ }
    trackConversion('app_payment_unlocked', { units, billing, value: total, currency: 'EUR' }, 'AddPaymentInfo');
  }, [data, signed, paid, units, billing, total]);

  if (isLoading || !data) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal-500" size={32} /></div>;
  }

  const { document, acceptance } = data.agreement;

  const handleAccept = async () => {
    await accept.mutateAsync(typedName.trim() || user?.name || '');
    track('app_agreement_accept');
  };

  // Switch the website model and persist it so the admin + server reflect the
  // choice even before payment.
  const changeSiteModel = (model: SiteModel) => {
    setSiteModel(model);
    save.mutate({ siteModel: model });
  };

  const pay = async () => {
    trackConversion('app_checkout_start', { units, billing, value: total, currency: 'EUR' }, 'InitiateCheckout');
    const res = await checkout.mutateAsync({ units, billing, siteModel });
    if (res.url) window.location.href = res.url;
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
          <FileText size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('portal.agreement.title')}</h1>
          <p className="text-slate-500 mt-0.5">{t('portal.agreement.combinedSubtitle', 'Firma tu acuerdo y elige tu plan en un solo paso.')}</p>
        </div>
      </div>

      {/* ── Step 1: Agreement ── */}
      {signed && acceptance ? (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800">
          <CheckCircle2 size={22} className="shrink-0" />
          <div className="text-sm">
            <p className="font-semibold">{t('portal.agreement.signedTitle')}</p>
            <p className="text-emerald-700">{t('portal.agreement.signedBy', { name: acceptance.accepted_name, date: new Date(acceptance.accepted_at).toLocaleDateString() })}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">{document.title}</h2>
              <span className="text-xs text-slate-400">v{document.version}</span>
            </div>
            <div
              className="px-6 py-5 prose prose-sm max-w-none text-slate-700 max-h-[42vh] overflow-y-auto
                [&_h3]:text-slate-900 [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-1.5 [&_h3]:text-base
                [&_p]:mb-3 [&_p]:leading-relaxed [&_strong]:text-slate-900"
              dangerouslySetInnerHTML={{ __html: document.bodyHtml }}
            />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('portal.agreement.fullNameLabel')}</label>
              <input type="text" value={typedName} onChange={e => setTypedName(e.target.value)}
                placeholder={user?.name || t('portal.agreement.fullNamePlaceholder')}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} className="mt-1 w-4 h-4 accent-teal-600" />
              <span className="text-sm text-slate-600">{t('portal.agreement.consent')}</span>
            </label>
            {accept.isError && <p className="text-sm text-rose-600">{t('portal.agreement.error')}</p>}
            <button onClick={handleAccept} disabled={!checked || accept.isPending}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors">
              {accept.isPending ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
              {t('portal.agreement.accept')}
            </button>
            <p className="text-xs text-slate-400 text-center">{t('portal.agreement.emailCopyNote')}</p>
          </div>
        </>
      )}

      {/* ── Step 2: Payment (unlocks once signed) ── */}
      {signed && !paid && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
          <div className="flex items-center gap-2 text-slate-900">
            <CreditCard size={18} className="text-teal-600" />
            <h2 className="font-semibold">{t('portal.payment.title', 'Elige tu plan')}</h2>
          </div>

          {/* Properties stepper — default 1 */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">{t('portal.payment.unitsLabel', 'Alojamientos')}</span>
            <div className="flex items-center gap-3">
              <button onClick={() => setUnits(u => Math.max(1, u - 1))} aria-label="-" className="w-9 h-9 rounded-lg border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-50"><Minus size={16} /></button>
              <span className="w-8 text-center font-bold text-slate-900 tabular-nums">{units}</span>
              <button onClick={() => setUnits(u => Math.min(MAX_UNITS, u + 1))} aria-label="+" className="w-9 h-9 rounded-lg border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-50"><Plus size={16} /></button>
            </div>
          </div>

          {/* Billing toggle */}
          <div className="flex rounded-lg bg-slate-100 p-1 text-sm">
            <button onClick={() => setBilling('monthly')} className={`flex-1 py-2 rounded-md font-medium transition-colors ${billing === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>{t('portal.payment.monthly', 'Mensual')}</button>
            <button onClick={() => setBilling('yearly')} className={`flex-1 py-2 rounded-md font-medium transition-colors ${billing === 'yearly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>{t('portal.payment.yearly', 'Anual')} · {t('portal.payment.yearlyHint', '1 mes gratis')}</button>
          </div>

          {/* Website model — combined (included) vs one site per property (+€189 each, one-time) */}
          <div className="space-y-2">
            <span className="text-sm font-medium text-slate-700">{t('portal.payment.siteModelLabel', 'Modelo de web')}</span>
            <div className="grid gap-2">
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${siteModel === 'combined' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <input type="radio" name="siteModel" checked={siteModel === 'combined'} onChange={() => changeSiteModel('combined')} className="mt-0.5 w-4 h-4 accent-teal-600" />
                <span className="text-sm text-slate-700">{t('portal.payment.siteModelCombined', 'Una sola web para todas mis propiedades (incluida)')}</span>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${siteModel === 'per_property' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <input type="radio" name="siteModel" checked={siteModel === 'per_property'} onChange={() => changeSiteModel('per_property')} className="mt-0.5 w-4 h-4 accent-teal-600" />
                <span className="text-sm text-slate-700">{t('portal.payment.siteModelPerProperty', 'Una web independiente por propiedad (+189€ por propiedad adicional, pago único)')}</span>
              </label>
            </div>
          </div>

          {/* Price summary */}
          <div className="border-t border-slate-100 pt-4 space-y-2">
            <div className="flex items-end justify-between">
              <span className="text-sm text-slate-500">{billing === 'yearly' ? t('portal.payment.perYear', 'al año') : t('portal.payment.perMonth', 'al mes')}</span>
              <span className="text-3xl font-extrabold text-slate-900 tabular-nums">{total}€</span>
            </div>
            {designFee > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">{t('portal.payment.designFeeLine', 'Diseño web por propiedad (pago único)')}</span>
                <span className="font-semibold text-slate-900 tabular-nums">+{designFee}€</span>
              </div>
            )}
          </div>

          <button onClick={pay} disabled={checkout.isPending}
            className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white font-semibold rounded-xl transition-colors">
            {checkout.isPending ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
            {t('portal.payment.pay', 'Pagar de forma segura')}
          </button>
          <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1.5">
            <ShieldCheck size={13} /> {t('portal.payment.guaranteeNote', 'Garantía de devolución de 30 días · sin permanencia')}
          </p>
        </div>
      )}

      {/* ── Done: continue to details ── */}
      {paid && (
        <button onClick={() => navigate('/portal/onboarding')}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors">
          {t('portal.agreement.continueSetup', 'Continuar con los detalles')} <ArrowRight size={18} />
        </button>
      )}
    </div>
  );
}
